import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../utils";
import { Eye } from "lucide-react";

export default function FreshLeads() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  // search
  const [q, setQ] = useState("");
  const [searchText, setSearchText] = useState("");

  // responsive table height
  const [tableHeight, setTableHeight] = useState(() =>
    Math.max(320, window.innerHeight - 220)
  );

  useEffect(() => {
    const onResize = () => setTableHeight(Math.max(320, window.innerHeight - 260));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const fetchSummary = useCallback(
    async (p = 1, pageSize = limit, search = searchText) => {
      try {
        setLoading(true);

        const res = await axios.get(`${BASE_URL}/api/legacy/fresh-leads/summary`, {
          params: { page: p, limit: pageSize, search: search || undefined },
        });

        const data = Array.isArray(res.data?.data) ? res.data.data : [];
        setRows(data);

        const meta = res.data?.meta || {};

        // ✅ robust total parsing (handles wrong/missing meta)
        const parsedTotalRaw =
          meta.total ?? meta.totalCount ?? meta.count ?? meta.records ?? null;

        const parsedTotal =
          parsedTotalRaw === null || parsedTotalRaw === undefined
            ? NaN
            : Number(parsedTotalRaw);

        // if backend total missing, fallback to rows length for display
        const safeTotal = Number.isFinite(parsedTotal) ? parsedTotal : data.length;

        setTotal(safeTotal);
        setPage(Number(meta.page || p));
        setLimit(Number(meta.limit || pageSize));
      } catch (e) {
        console.error("fresh leads summary error", e);
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [limit, searchText]
  );

  // ✅ correct dependencies
  useEffect(() => {
    fetchSummary(1, limit, searchText);
  }, [fetchSummary, limit, searchText]);

  // Pagination UI helpers
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit || 1)), [total, limit]);

  // ✅ if total is wrong but rows exist, still show proper “Showing”
  const displayTotal = rows.length > 0 && total === 0 ? rows.length : total;

  const startRow = displayTotal === 0 ? 0 : (page - 1) * limit + 1;
  const endRow = Math.min(displayTotal, page * limit);

  const goPage = (p) => {
    const np = Math.max(1, Math.min(totalPages, p));
    fetchSummary(np, limit, searchText);
  };
  const goFirst = () => page > 1 && goPage(1);
  const goPrev = () => page > 1 && goPage(page - 1);
  const goNext = () => page < totalPages && goPage(page + 1);
  const goLast = () => page < totalPages && goPage(totalPages);

  function renderPageButtons() {
    const maxButtons = 3;
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, page - half);
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) start = Math.max(1, end - maxButtons + 1);

    const buttons = [];
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => goPage(i)}
          className={`px-3 py-1 border rounded text-sm ${
            i === page ? "bg-blue-600 text-white" : "bg-white"
          }`}
          aria-current={i === page ? "page" : undefined}
        >
          {i}
        </button>
      );
    }
    return buttons;
  }

  const [pageInput, setPageInput] = useState("");
  const handleJumpSubmit = (e) => {
    e.preventDefault();
    const n = Number(pageInput);
    if (!n || n < 1) return;
    goPage(n);
    setPageInput("");
  };

  const handleSearch = () => {
    const s = q.trim();
    setSearchText(s);
    setPage(1);
    fetchSummary(1, limit, s);
  };

  const handleRefresh = () => {
    fetchSummary(page, limit, searchText);
  };

  return (
    <div className="p-4 flex flex-col text-[var(--fg)]">
      {/* Toolbar */}
      <div className="filter-panel mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="page-kicker">Legacy</div>
            <h2 className="page-title truncate">Fresh Leads (Duplicate)</h2>
            <div className="page-subtitle">
              Grouped by name (click eye to view all records)
            </div>
            <div className="text-xs text-[var(--fg)] mt-1">
              Total records: <strong>{displayTotal}</strong>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap md:flex-nowrap md:ml-4">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name..."
              className="ui-input w-full md:w-56"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />

            <button
              onClick={handleSearch}
              className="ui-btn ui-btn-primary"
              disabled={loading}
              title="Search on server"
            >
              Search
            </button>

            <button
              onClick={handleRefresh}
              className="ui-btn"
              disabled={loading}
              title="Refresh current page"
            >
              Refresh
            </button>

            <select
              value={limit}
              onChange={(e) => {
                const n = Number(e.target.value);
                setLimit(n);
                setPage(1);
                fetchSummary(1, n, searchText);
              }}
              className="ui-select"
              title="Rows per page"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] overflow-hidden" style={{ height: tableHeight }}>
        <div className="h-full overflow-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-[var(--surface-muted)] border-b border-[var(--border)] sticky top-0 z-10">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--fg-muted)]">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--fg-muted)] w-32">Count</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-[var(--fg-muted)] w-28">Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td className="p-4 text-slate-500" colSpan={3}>
                    {loading ? "Loading..." : "No records"}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.name} className="border-b hover:bg-[var(--surface-muted)]">
                    <td className="px-4 py-3">{r.name}</td>
                    <td className="px-4 py-3">{r.count}</td>
                    <td className="px-4 py-3 text-center">
                      <a
                        href={`/legacy/fresh-leads/${encodeURIComponent(r.name)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View records"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-green-500 text-white hover:bg-green-600 transition"
                      >
                        <Eye className="w-4 h-4 text-white" />
                      </a>


                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="pagination-bar flex flex-col md:flex-row items-center justify-between gap-3 mt-4">
        <div className="text-sm text-slate-600">
          {displayTotal === 0 ? (
            <>No records</>
          ) : (
            <>
              Showing <strong>{startRow}</strong>–<strong>{endRow}</strong> of{" "}
              <strong>{displayTotal}</strong>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={goFirst}
            disabled={page <= 1 || loading}
            className="px-3 py-1 border rounded disabled:opacity-60 text-sm"
          >
            « First
          </button>

          <button
            onClick={goPrev}
            disabled={page <= 1 || loading}
            className="px-3 py-1 border rounded disabled:opacity-60 text-sm"
          >
            ◀ Prev
          </button>

          <div className="flex items-center gap-1">{renderPageButtons()}</div>

          <button
            onClick={goNext}
            disabled={page >= totalPages || loading}
            className="px-3 py-1 border rounded disabled:opacity-60 text-sm"
          >
            Next ▶
          </button>

          <button
            onClick={goLast}
            disabled={page >= totalPages || loading}
            className="px-3 py-1 border rounded disabled:opacity-60 text-sm"
          >
            Last »
          </button>

          <div className="ml-2 text-sm text-slate-600">
            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
          </div>

          <form onSubmit={handleJumpSubmit} className="flex items-center gap-1 ml-2">
            <input
              type="number"
              min="1"
              max={totalPages}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              placeholder="Go to"
              className="ui-input w-20"
            />
            <button type="submit" className="px-3 py-1 border border-[var(--border)] rounded text-sm">
              Go
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
